import React, { useEffect, useCallback, useState } from 'react';
import { Redirect } from 'react-router-dom';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
	colors, Button, TextField, Typography,
	Grid, Select, Card, CardHeader, CardContent,
	Divider, Accordion, AccordionSummary, Checkbox,
	AccordionDetails, FormControlLabel, Tabs, Tab,
	FormControl, FormGroup
} from '@material-ui/core';
import { Page, Header, Map, SelectField, Label, SearchBar, FiltersDrawer } from 'components';
import { enqueueSnackbar } from 'actions';
import {
	SetFormState, GetAlgorithmsDates, RunOvershootingByCoordinates, RunOvershootingByFile, UploadFileThenRunOvershooting
} from '../../actions/AlgorithmsActions';
import ByCoordinates from './components/ByCoordinates';
import ByFile from './components/ByFile';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const Overshooting = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.algorithmsReducer.formState);
	const algoCities = useSelector((state) => state.algorithmsReducer.algoCities);
	const algoDates = useSelector((state) => state.algorithmsReducer.algoDates);
	const userId = useSelector(state => state.session.user.id);

	const [state, setState] = React.useState({
		tech2g: false,
		tech3g: false,
		tech4g: false,
	});

	const { tech2g, tech3g, tech4g } = state;

	const tabs = [
		{ value: 'byCoordinates', label: 'Coordinates' },
		{ value: 'byFile', label: 'Sites' },
	];

	const osParameters2g = {
		numberOfBins: formState.values.numberOfBins2g,
		powerDelta: formState.values.powerDelta2g,
		clusterRadius: formState.values.clusterRadius2g,
		warningPwrDelta: formState.values.warningPwrDelta2g,
		powerThreshold: formState.values.powerThreshold2g,
		qualityDelta: formState.values.qualityDelta2g,
		qualityThreshold: formState.values.qualityThreshold2g,
		warningQualityDelta: formState.values.warningQualityDelta2g
	};

	const osParameters3g = {
		numberOfBins: formState.values.numberOfBins3g,
		powerDelta: formState.values.powerDelta3g,
		clusterRadius: formState.values.clusterRadius3g,
		warningPwrDelta: formState.values.warningPwrDelta3g,
		powerThreshold: formState.values.powerThreshold3g,
		qualityDelta: formState.values.qualityDelta3g,
		qualityThreshold: formState.values.qualityThreshold3g,
		warningQualityDelta: formState.values.warningQualityDelta3g
	};

	const osParameters4g = {
		numberOfBins: formState.values.numberOfBins4g,
		powerDelta: formState.values.powerDelta4g,
		clusterRadius: formState.values.clusterRadius4g,
		warningPwrDelta: formState.values.warningPwrDelta4g,
		powerThreshold: formState.values.powerThreshold4g,
		qualityDelta: formState.values.qualityDelta4g,
		qualityThreshold: formState.values.qualityThreshold4g,
		warningQualityDelta: formState.values.warningQualityDelta4g
	};

	const [tab, setTab] = useState('byCoordinates');

	const _handleOsParametersChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]: event.target.value
			},
		});
	};

	const _handleTechChange = (event) => {
		setState({ ...state, [event.target.name]: event.target.checked });
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]: event.target.checked
			},
		});
	};

	const _handleChangeCity = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			}
		});
		dispatch(GetAlgorithmsDates(event.target.value));
	};

	const _handleChangeDate = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			}
		});
	};

	const _runAlgorithm = (event) => {
		const points = {
			northEastLatitude: formState.values.northEastLatitude,
			northEastLongitude: formState.values.northEastLongitude,
			southWestLatitude: formState.values.southWestLatitude,
			southWestLongitude: formState.values.southWestLongitude
		};

		if (!formState.values.tech2g && !formState.values.tech3g && !formState.values.tech4g) {
			dispatch(enqueueSnackbar({ message: 'Please select one or more Technologies', options: { variant: 'Warning' } }));
		} else if (formState.values.algoSelectedCity === '') {
			dispatch(enqueueSnackbar({ message: 'Please select a City', options: { variant: 'Warning' } }));
		} else if (formState.values.algoSelectedDate === '') {
			dispatch(enqueueSnackbar({ message: 'Please select a Date', options: { variant: 'Warning' } }));
		} else if (formState.values.algoSelectedTab === 'byCoordinates') {
			dispatch(RunOvershootingByCoordinates(
				userId, formState.values.algoSelectedCity, formState.values.algoSelectedDate, points,
				formState.values.tech2g, formState.values.tech3g, formState.values.tech4g,
				osParameters2g, osParameters3g, osParameters4g
			));
		} else if (formState.values.algoSelectedTab === 'byFile') {
			if (formState.values.algoInputFilePath.size > 50000000) {
				dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 50MB', options: { variant: 'warning' } }));
			} else if (formState.values.algoInputFilePath === '') {
				dispatch(enqueueSnackbar({ message: 'No file selected', options: { variant: 'Info' } }));
			} else {
				dispatch(UploadFileThenRunOvershooting(
					userId, formState.values.algoSelectedCity, formState.values.algoSelectedDate,
					formState.values.algoInputFilePath, formState.values.tech2g, formState.values.tech3g, formState.values.tech4g,
					osParameters2g, osParameters3g, osParameters4g
				));
			}
		}
	};

	const handleTabsChange = (event, value) => {
		setTab(value);
		setFormState({
			...formState,
			values: {
				...formState.values,
				algoSelectedTab: value
			}
		});
	};

	if (!tab) {
		return <Redirect to="/home" />;
	}

	if (!tabs.find((t) => t.value === tab)) {
		return <Redirect to="/errors/error-404" />;
	}

	return (
		<div className={classes.fields}>
			{/* New row */}
			<Grid container spacing={1}>
				<Grid item md={12} xs={12}>
					<Card>
						{/* <CardHeader
							title="Technology"
						/>
						<Divider /> */}
						<CardContent>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<FormControl component="fieldset">
										{/* <FormLabel component="legend">Technology</FormLabel> */}
										<FormGroup aria-label="position" row>
											<FormControlLabel
												value="2g"
												control={<Checkbox color="primary" name="tech2g" onChange={_handleTechChange} checked={tech2g} />}
												label="2G"
												labelPlacement="top"
											/>
											<FormControlLabel
												value="3g"
												control={<Checkbox color="primary" name="tech3g" onChange={_handleTechChange} checked={tech3g} />}
												label="3G"
												labelPlacement="top"
												on
											/>
											<FormControlLabel
												value="4g"
												control={<Checkbox color="primary" name="tech4g" onChange={_handleTechChange} checked={tech4g} />}
												label="4G"
												labelPlacement="top"
											/>
										</FormGroup>
									</FormControl>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="algoSelectedCity"
										name="algoSelectedCity"
										hintText="Cities"
										defaultValue={formState.values.algoSelectedCity || ''}
										menuItems={algoCities}
										onChange={_handleChangeCity}
										value={formState.values.algoSelectedCity || ''}
									// error={_hasError('selectedCity') ? formState.errors.selectedCity[0] : null}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="algoSelectedDate"
										name="algoSelectedDate"
										hintText="Date"
										defaultValue={formState.values.algoSelectedDate || ''}
										menuItems={algoDates}
										onChange={_handleChangeDate}
										value={formState.values.algoSelectedDate || ''}
									// error={_hasError('selectedCity') ? formState.errors.selectedCity[0] : null}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={1}>
				<Grid item md={12} xs={12}>
					<Card>
						{/* <CardHeader
							title="Technology"
						/>
						<Divider /> */}
						<CardContent>
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="numberOfBins"
										name="numberOfBins"
										label="Number Of Bins"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="numberOfBins2g"
										name="numberOfBins2g"
										label="2G"
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.numberOfBins2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="numberOfBins3g"
										name="numberOfBins3g"
										label="3G"
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.numberOfBins3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="numberOfBins4g"
										name="numberOfBins4g"
										label="4G"
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.numberOfBins4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerDelta"
										name="powerDelta"
										label="Power Delta"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerDelta2g"
										name="powerDelta2g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.powerDelta2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerDelta3g"
										name="powerDelta3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.powerDelta3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerDelta4g"
										name="powerDelta4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.powerDelta4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="clusterRadius"
										name="clusterRadius"
										label="Cluster Radius"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="clusterRadius2g"
										name="clusterRadius2g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.clusterRadius2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="clusterRadius3g"
										name="clusterRadius3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.clusterRadius3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="clusterRadius4g"
										name="clusterRadius4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.clusterRadius4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningPwrDelta"
										name="warningPwrDelta"
										label="Warning Pwr Delta"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningPwrDelta2g"
										name="warningPwrDelta2g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.warningPwrDelta2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningPwrDelta3g"
										name="warningPwrDelta3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.warningPwrDelta3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningPwrDelta4g"
										name="warningPwrDelta4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.warningPwrDelta4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerThreshold"
										name="powerThreshold"
										label="Power Threshold"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerThreshold2g"
										name="powerThreshold2g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.powerThreshold2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerThreshold3g"
										name="powerThreshold3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.powerThreshold3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="powerThreshold4g"
										name="powerThreshold4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.powerThreshold4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityDelta"
										name="qualityDelta"
										label="Quality Delta"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityDelta2g"
										name="qualityDelta2g"
										label=""
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										fullWidth
										value={formState.values.qualityDelta2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityDelta3g"
										name="qualityDelta3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.qualityDelta3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityDelta4g"
										name="qualityDelta4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.qualityDelta4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityThreshold"
										name="qualityThreshold"
										label="Quality Threshold"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityThreshold2g"
										name="qualityThreshold2g"
										label=""
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										fullWidth
										value={formState.values.qualityThreshold2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityThreshold3g"
										name="qualityThreshold3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.qualityThreshold3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="qualityThreshold4g"
										name="qualityThreshold4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.qualityThreshold4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningQualityDelta"
										name="warningQualityDelta"
										label="Warning Quality Delta"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										fullWidth
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningQualityDelta2g"
										name="warningQualityDelta2g"
										label=""
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										fullWidth
										value={formState.values.warningQualityDelta2g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningQualityDelta3g"
										name="warningQualityDelta3g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.warningQualityDelta3g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="warningQualityDelta4g"
										name="warningQualityDelta4g"
										label=""
										// InputProps={{
										// 	readOnly: true,
										// }}
										variant="outlined"
										fullWidth
										value={formState.values.warningQualityDelta4g}
										onChange={_handleOsParametersChange}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={1}>
				<Grid item md={12} xs={12}>
					<Card>
						{/* <CardHeader
							title="Technology"
						/>
						<Divider /> */}
						<CardContent>
							<Grid container spacing={1}>
								<Grid item md={12} xs={12}>
									<Tabs
										className={classes.tabs}
										onChange={handleTabsChange}
										scrollButtons="auto"
										value={tab}
										variant="scrollable"
									>
										{tabs.map((t) => (
											<Tab key={t.value} label={t.label} value={t.value} />
										))}
									</Tabs>
									<Divider className={classes.divider} />
									<div className={classes.content}>
										{tab === 'byCoordinates' && <ByCoordinates />}
										{tab === 'byFile' && <ByFile />}
									</div>
								</Grid>
							</Grid>
							{/* New row */}
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<Button
										color="primary"
										onClick={_runAlgorithm}
										variant="contained"
									>
										Run
									</Button>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* New row */}
			<Grid container spacing={1}>
				<Grid item md={6} xs={6} />
			</Grid>
		</div>
	);
};

export default Overshooting;
